import { useQuery } from "@tanstack/react-query";
import type { Address, Hex } from "viem";
import { API_URL } from "./config";
import { demoApi, resolveMode } from "./demo";

export type ApiConfig = {
  chainId: number;
  explorer: string;
  contracts: {
    options: Address;
    exchange: Address;
    vault: Address;
    router: Address;
    treasury: Address;
    distributor: Address;
    timelock: Address;
    token: Address | null;
    weth: Address;
  };
  launch: { token: Address; curve: Address; phase: number; graduationThreshold: string; creatorTaxBps: number } | null;
  params: { bandBps: number; quoteTtlSec: number; epochHoursUtc: number[]; minHoldingUsd: number; expiryHourUtc: number };
};

export type Market = {
  launched: boolean;
  phase: number;
  spotWei: string;
  twapWei: string;
  priceEth: number;
  priceUsd: number;
  ethUsd: number;
  marketCapUsd: number;
  volume24hEth: number;
  change24hPct: number;
  graduation?: { raisedEth: number; thresholdEth: number; progress: number };
  realizedVol?: number;
};

export type ChainSeries = {
  id: string;
  isCall: boolean;
  strikeWei: string;
  strikeEth: number;
  expiry: number;
  askEth: number;
  delta: number;
  moneyness: number;
  openInterest: string;
  vaultCapacity: string;
  /** What the vault pays per option to buy it back (0 = no bid), and how many it will buy. */
  bidEth?: number;
  vaultBidCapacity?: string;
};

export type OptionChain = { open: boolean; sigma?: number; spotWei?: string; series: ChainSeries[] };

export type SeriesRow = { id: string; underlying: string; quote: string; strike: string; expiry: number; is_call: number };

export type Positions = {
  longs: { series: SeriesRow; amount: string }[];
  shorts: { series: SeriesRow; collateral: string; proceeds: string }[];
  autoExercise: boolean;
  routerApproved: boolean;
};

export type RewardsResp = { account: string; epochs: { epoch: number; endTs: number; amount: string; proof: Hex[]; claimed: boolean }[] };

export type Stats = {
  paidToHolders: string;
  fundedToHolders: string;
  allocatedUnclaimed: string;
  nextPayoutPot: string;
  premiumsWeth: string;
  optionsTraded: string;
  fills: number;
};

export type Activity = { block: number; ts: number; tx: Hex; name: string; args: Record<string, string> }[];

export type Candle = { t: number; o: number; h: number; l: number; c: number; v: number };

export type SignedQuote = {
  order: {
    maker: Address;
    taker: Address;
    seriesId: string;
    side: number;
    amount: string;
    price: string;
    premiumToken: Address;
    minSpot: string;
    maxSpot: string;
    deadline: string;
    nonce: string;
  };
  signature: Hex;
  premium: string;
  premiumToken: "WETH" | "STRIKE";
  askEthPerOption: number;
  sigma: number;
  spotWei: string;
  expiresAt: number;
};

/** A vault buyback quote: ETH per option, and what the seller receives after the fee. */
export type SellQuote = Omit<SignedQuote, "askEthPerOption" | "premiumToken"> & {
  side: "sell";
  premiumToken: "WETH";
  bidEthPerOption: number;
  proceeds: string;
};

export type UserOrder = {
  digest: Hex;
  maker: Address;
  series_id: string;
  side: number;
  amount: string;
  price: string;
  premium_token: Address;
  min_spot: string;
  max_spot: string;
  deadline: number;
  nonce: string;
  signature: Hex;
};

export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
  ) {
    super(message);
  }
}

export async function api<T>(path: string, init?: RequestInit): Promise<T> {
  if ((await resolveMode()) === "demo") return demoApi<T>(path, init);
  const res = await fetch(`${API_URL}${path}`, {
    ...init,
    headers: { "content-type": "application/json", ...(init?.headers ?? {}) },
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) throw new ApiError(res.status, body.error ?? "error", body.message ?? res.statusText);
  return body as T;
}

export const useApiConfig = () => useQuery({ queryKey: ["config"], queryFn: () => api<ApiConfig>("/api/config"), staleTime: 60_000 });
export const useMarket = () => useQuery({ queryKey: ["market"], queryFn: () => api<Market>("/api/market"), refetchInterval: 5_000 });
export const useOptionChain = () => useQuery({ queryKey: ["chain"], queryFn: () => api<OptionChain>("/api/chain"), refetchInterval: 10_000 });
export const useCandles = (tf: number) =>
  useQuery({ queryKey: ["candles", tf], queryFn: () => api<Candle[]>(`/api/candles?tf=${tf}`), refetchInterval: 15_000 });
export const useStats = () => useQuery({ queryKey: ["stats"], queryFn: () => api<Stats>("/api/stats"), refetchInterval: 30_000 });
export const useActivity = () => useQuery({ queryKey: ["activity"], queryFn: () => api<Activity>("/api/activity?limit=20"), refetchInterval: 15_000 });
export const usePositions = (addr?: Address) =>
  useQuery({
    queryKey: ["positions", addr],
    queryFn: () => api<Positions>(`/api/positions/${addr}`),
    enabled: !!addr,
    refetchInterval: 10_000,
  });
export const useRewards = (addr?: Address) =>
  useQuery({
    queryKey: ["rewards", addr],
    queryFn: () => api<RewardsResp>(`/api/rewards/${addr}`),
    enabled: !!addr,
    refetchInterval: 30_000,
  });
export const useUserOrders = (seriesId?: string) =>
  useQuery({
    queryKey: ["orders", seriesId ?? "all"],
    queryFn: () => api<UserOrder[]>(seriesId ? `/api/orders?seriesId=${seriesId}` : "/api/orders"),
    refetchInterval: 15_000,
  });

export const requestQuote = (seriesId: string, amount: bigint, premiumToken: "WETH" | "STRIKE" = "WETH") =>
  api<SignedQuote>("/api/quote", {
    method: "POST",
    body: JSON.stringify({ seriesId, amount: amount.toString(), premiumToken }),
  });

/** A signed vault buyback: the user sells `amount` options for ETH. */
export const requestSellQuote = (seriesId: string, amount: bigint) =>
  api<SellQuote>("/api/quote", {
    method: "POST",
    body: JSON.stringify({ seriesId, amount: amount.toString(), side: "sell" }),
  });

export const postOrder = (order: SignedQuote["order"], signature: Hex) =>
  api<{ digest: Hex }>("/api/orders", { method: "POST", body: JSON.stringify({ order, signature }) });

/** Converts a quote's string order into the tuple the contracts expect. */
export function orderTuple(o: SignedQuote["order"]) {
  return {
    maker: o.maker,
    taker: o.taker,
    seriesId: BigInt(o.seriesId),
    side: o.side,
    amount: BigInt(o.amount),
    price: BigInt(o.price),
    premiumToken: o.premiumToken,
    minSpot: BigInt(o.minSpot),
    maxSpot: BigInt(o.maxSpot),
    deadline: BigInt(o.deadline),
    nonce: BigInt(o.nonce),
  } as const;
}
